import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EditLegendContainer } from '../../Legend';
import { LegendItem } from '../../../components/Legend';
import styles from './HarvestAndPlantingLegend.css';
import {
  reqLegend as requestLegendAction,
  toggleEditLegend as toggleEditLegendAction
} from '../../../actions/legendActions';
import { trackEvent } from '../../../actions/analyticsActions';
import { getLayerUnits } from '../../../config/legendUnits';
import {
  getCropNominalWeight,
  processIntervals,
} from '../../../utils/processUnits';
import { usernameSelector, userSelector } from '../../../selectors/userSelectors';
import { PLANTING, HARVEST } from '../../../constants/mapLayerIds';
import { LOCALE_GROUPS, HARVEST_YIELD_UNITS } from '../../../constants/units';
import { DMB_WEB_AG_VIZ } from '../../../constants/featureFlags';
import { SILAGE_CORN_CROP_CODE, SUGARCANE_CROP_CODE, COTTON_CROP_CODE } from '../../../constants/cropCodes';
import { Interval, EventDates, User } from '../../../types';
import { PartialWithRequired } from '../../../../types/utils';

const getCropUnits = (cropCode, activeMapLayer, localeGroup) => {
  const hasSilageCornCrop = cropCode === SILAGE_CORN_CROP_CODE;
  const hasSugarCaneCrop = cropCode === SUGARCANE_CROP_CODE;

  if (activeMapLayer.id === HARVEST.YIELD && (hasSilageCornCrop || hasSugarCaneCrop)) {
    const newLocaleGroup = cropCode === SILAGE_CORN_CROP_CODE ? LOCALE_GROUPS.YIELD_SILAGE :
      LOCALE_GROUPS.YIELD_SUGARCANE;
    return { key: HARVEST_YIELD_UNITS.METRICTONS_PER_HECTARE, localeGroup: newLocaleGroup };
  }

  if (activeMapLayer.id === HARVEST.YIELD && (cropCode === COTTON_CROP_CODE)) {
    return { key: HARVEST_YIELD_UNITS.METRICTONS_PER_HECTARE, localeGroup: LOCALE_GROUPS.YIELD_COTTON };
  }

  return getLayerUnits(activeMapLayer.id, localeGroup);
};

export interface HarvestAndPlantingLegendProps {
  agVizEnabled?: boolean;
  crop: string;
  cropCode?: string;
  currentUserHasFetched: boolean;
  dates: EventDates;
  fieldUuid: string,
  index: number;
  intervals?: Interval[];
  isFetching?: boolean;
  layer: string;
  locale: string;
  reqLegend?: typeof requestLegendAction;
  season: string;
  testId: string;
  toggleEditLegend?: typeof toggleEditLegendAction;
  trackAnalyticsEvent: (noun: string, verb: string, data: object) => unknown;
  units?: object;
  unitsLocaleGroup?: string;
  username: string;
  user: PartialWithRequired<User, 'uuid'>;
}

export const HarvestAndPlantingLegend = ({
  agVizEnabled,
  crop,
  cropCode,
  currentUserHasFetched,
  dates,
  fieldUuid,
  index,
  isFetching,
  layer,
  locale,
  reqLegend: reqLegendCallback,
  season,
  testId,
  toggleEditLegend,
  trackAnalyticsEvent,
  units,
  unitsLocaleGroup,
  username,
  user,
  intervals = []
}: HarvestAndPlantingLegendProps) => {
  useEffect(() => {
    const useLegacyLegends = !intervals.length && reqLegendCallback && !agVizEnabled

    if (useLegacyLegends) {
      reqLegendCallback(username, fieldUuid, dates, crop, layer, season, undefined, user.uuid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevPropsRef = useRef({ layer, season });
  useEffect(() => {
    const { layer: prevLayer, season: prevSeason } = prevPropsRef.current;

    // Don't request legend if Ag-Viz feature flag is enabled - Ag-Viz saga will provide it
    if (
      (prevLayer !== layer || prevSeason !== season) &&
      !intervals.length
      && reqLegendCallback
      && !agVizEnabled
    ) {
      reqLegendCallback(username, fieldUuid, dates, crop, layer, season, undefined, user.uuid);
    }

    prevPropsRef.current = { layer, season };
  }, [layer, season, intervals, username, fieldUuid, dates, crop, user?.uuid, reqLegendCallback, agVizEnabled]);

  const toggleEditLegendCallback = () => {
    if (toggleEditLegend) {
      toggleEditLegend(index);
    }
    trackAnalyticsEvent('edit_legend', 'clicked', {
      numberOfSteps: intervals.length,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      min: intervals[0] && intervals[0].value,
      max:
        intervals[intervals.length - 1] &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        intervals[intervals.length - 1].value,
    });
  };

  if (!currentUserHasFetched || !intervals.length) {
    return <LegendItem isFetching/>;
  }

  const finalIntervals = processIntervals(intervals, units, locale, cropCode);
  const unitHasKey = Boolean(finalIntervals.key);

  return (
    <div
      className={styles.legendContainer}
      data-test-id={`${testId}-harvestPlanting-${layer}`}
    >
      <EditLegendContainer
        crop={crop}
        dates={dates}
        index={index}
        intervals={finalIntervals.intervals}
        units={finalIntervals}
        unitsLocaleGroup={unitsLocaleGroup}
        year={dates.year}
      />
      <LegendItem
        intervals={finalIntervals.intervals}
        isEditable={!agVizEnabled && (unitHasKey || layer === PLANTING.CEC)}
        isFetching={isFetching ?? false}
        toggleEditLegend={toggleEditLegendCallback}
        units={finalIntervals.key}
        agVizEnabled={agVizEnabled}
      />
    </div>
  );
}

function mapStateToProps(state, props) {
  const activeSeason = state.seasons.activeSeasons[props.index];
  const activeMapLayer = state.layers.activeMapLayers[props.index];
  const { legend, localeGroup } =
    state.layers.list[activeSeason.id][activeMapLayer.id];

  const { crop, cropCustomName } = state.seasons.list[activeSeason.id];
  const cropCode = state.crops[crop] && state.crops[crop].code;
  const units = getCropUnits(cropCode, activeMapLayer, localeGroup);
  const cropNominalWeight = getCropNominalWeight(crop, state.crops);
  
  // Check if Ag-Viz feature flag is enabled
  const agVizEnabled = state.feature?.userConfigFeatures?.[DMB_WEB_AG_VIZ]?.value || false;
  return {
    agVizEnabled,
    crop: cropCustomName,
    cropCode,
    currentUserHasFetched: state.currentUser.hasFetched,
    fieldUuid: state.field.uuid.toUpperCase(),
    intervals: legend,
    isFetching: state.legend.isFetching,
    layer: activeMapLayer.id,
    locale: state.app.locale,
    season: activeSeason.id,
    username: usernameSelector(state),
    units: { ...units, cropNominalWeight },
    unitsLocaleGroup: localeGroup,
    user: userSelector(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      reqLegend: requestLegendAction,
      toggleEditLegend: toggleEditLegendAction,
      trackAnalyticsEvent: trackEvent,
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HarvestAndPlantingLegend);
